import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  GearSix, Sun, Moon, Lock, Bell, Eye, EyeSlash, Check
} from '@phosphor-icons/react';
import { SignedInDevicesPanel } from '../components/SignedInDevicesPanel';
import { AdminSessionsPanel } from '../components/AdminSessionsPanel';

export default function SettingsPage() {
  const { user, apiCall } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Password change
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState({ type: '', text: '' });

  // Notification prefs — persisted server-side
  const [notifPrefs, setNotifPrefs] = useState({
    email_on_share: true,
    in_app_notifications: true,
  });
  const [notifLoading, setNotifLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiCall('get', '/auth/notification-preferences');
        if (!cancelled && res?.data) {
          setNotifPrefs(p => ({ ...p, ...res.data }));
        }
      } catch { /* keep defaults */ }
      finally { if (!cancelled) setNotifLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [apiCall]);

  async function saveNotifPref(key, value) {
    const prev = notifPrefs;
    setNotifPrefs(p => ({ ...p, [key]: value }));
    setSavingKey(key);
    try {
      const res = await apiCall('patch', '/auth/notification-preferences', { [key]: value });
      if (res?.data) setNotifPrefs(p => ({ ...p, ...res.data }));
      toast.success('Preferences updated');
    } catch (err) {
      setNotifPrefs(prev);
      toast.error(err.response?.data?.detail || 'Failed to update preferences');
    } finally { setSavingKey(null); }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    setPwMsg({ type: '', text: '' });
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwMsg({ type: 'error', text: 'New passwords do not match.' }); return;
    }
    if (pwForm.new_password.length < 6) {
      setPwMsg({ type: 'error', text: 'Password must be at least 6 characters.' }); return;
    }
    setPwLoading(true);
    try {
      await apiCall('post', '/auth/change-password', {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password
      });
      setPwMsg({ type: 'success', text: 'Password changed successfully.' });
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setPwMsg({ type: 'error', text: err.response?.data?.detail || 'Failed to change password.' });
    } finally { setPwLoading(false); }
  }

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6 animate-fadeIn" data-testid="settings-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <GearSix size={22} className="text-primary" weight="duotone" />
        </div>
        <div>
          <h1 className="text-xl font-semibold">Settings</h1>
          <p className="text-sm text-muted-foreground">Manage your account preferences</p>
        </div>
      </div>

      {/* Appearance */}
      <Card className="shadow-card rounded-lg border-0">
        <CardContent className="p-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">Appearance</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Theme</p>
              <p className="text-xs text-muted-foreground mt-0.5">Choose light or dark mode</p>
            </div>
            <div className="flex items-center gap-2 bg-secondary rounded-lg p-1">
              <button
                onClick={() => theme !== 'light' && toggleTheme()}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${theme === 'light' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="theme-light-btn"
              >
                <Sun size={14} weight="bold" /> Light
              </button>
              <button
                onClick={() => theme !== 'dark' && toggleTheme()}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${theme === 'dark' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="theme-dark-btn"
              >
                <Moon size={14} weight="bold" /> Dark
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="shadow-card rounded-lg border-0">
        <CardContent className="p-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">
            <Lock size={14} className="inline mr-1.5" weight="bold" />Change Password
          </h2>
          {pwMsg.text && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${pwMsg.type === 'success' ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400'}`} data-testid="pw-message">
              {pwMsg.type === 'success' && <Check size={14} className="inline mr-1" weight="bold" />}{pwMsg.text}
            </div>
          )}
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium">Current Password</Label>
              <div className="relative">
                <Input type={showPw.current ? 'text' : 'password'} value={pwForm.current_password} onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))} required className="pr-10" data-testid="current-pw-input" />
                <button type="button" onClick={() => setShowPw(p => ({ ...p, current: !p.current }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{showPw.current ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[13px] font-medium">New Password</Label>
                <div className="relative">
                  <Input type={showPw.new ? 'text' : 'password'} value={pwForm.new_password} onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))} required placeholder="Min 6 characters" className="pr-10" data-testid="new-pw-input" />
                  <button type="button" onClick={() => setShowPw(p => ({ ...p, new: !p.new }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{showPw.new ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-medium">Confirm Password</Label>
                <Input type="password" value={pwForm.confirm_password} onChange={e => setPwForm(p => ({ ...p, confirm_password: e.target.value }))} required placeholder="Re-enter" data-testid="confirm-pw-input" />
              </div>
            </div>
            <Button type="submit" disabled={pwLoading} className="rounded-md" data-testid="change-pw-btn">
              {pwLoading ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card className="shadow-card rounded-lg border-0">
        <CardContent className="p-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">
            <Bell size={14} className="inline mr-1.5" weight="bold" />Notifications
          </h2>
          <div className="space-y-4">
            {[
              { key: 'email_on_share', label: 'Email when a project is shared with me', desc: 'Get notified by email when someone shares a project' },
              { key: 'in_app_notifications', label: 'In-app notifications', desc: 'Show notification badge and dropdown in the app' },
            ].map(item => (
              <div key={item.key} className="flex items-center justify-between py-1">
                <div className="pr-4">
                  <p className="text-sm font-medium">{item.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                </div>
                <Switch
                  checked={!!notifPrefs[item.key]}
                  disabled={notifLoading || savingKey === item.key}
                  onCheckedChange={(v) => saveNotifPref(item.key, v)}
                  data-testid={`toggle-${item.key}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Signed-in devices — surfaces the caller's current active session
          (IP, UA, timestamps) with a self-serve sign-out button. */}
      <SignedInDevicesPanel />

      {/* Admins get a tenant-wide (or org-wide for super_admin) session
          table with a per-user Sign-out button. */}
      {(user?.role === 'super_admin' || user?.role === 'tenant_admin') && (
        <AdminSessionsPanel />
      )}

      {/* Account Info */}
      <Card className="shadow-card rounded-lg border-0">
        <CardContent className="p-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">Account</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="font-medium">{user?.email}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Role</span><span className="font-medium capitalize">{user?.role === 'super_admin' ? 'Admin' : user?.role === 'tenant_admin' ? 'Tenant' : 'User'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Name</span><span className="font-medium">{user?.name}</span></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
